import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import { ChevronDown, Check } from 'lucide-react'

export interface FilterOption {
  value: string
  /** Display label shown in the dropdown and active-chip rail. */
  label: string
  /** Optional leading element (icon, avatar, etc.) */
  leading?: React.ReactNode
}

interface FilterDropdownProps {
  /** Trigger label, e.g. "Category". Active count appended in the trigger. */
  label: string
  options: FilterOption[]
  /** Currently selected values. */
  selected: Set<string>
  /** Toggle a single option. Parent reconciles into store. */
  onToggle: (value: string) => void
  /** Clear all selections for this filter group. */
  onClear: () => void
  className?: string
}

/**
 * Multi-select filter dropdown using Popover + Checkbox rows.
 *
 * The trigger shows the label plus an active count Badge when 1+ options
 * are selected. Clicking checkboxes does NOT close the popover so the user
 * can pick multiple values in one open.
 */
export function FilterDropdown({
  label,
  options,
  selected,
  onToggle,
  onClear,
  className,
}: FilterDropdownProps) {
  const [open, setOpen] = useState(false)
  const count = selected.size
  const isActive = count > 0

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-9 gap-1.5',
            isActive && 'border-[hsl(var(--brand))] text-foreground',
            className
          )}
        >
          {label}
          {isActive && (
            <Badge
              variant="secondary"
              className="ml-0.5 h-4 px-1 text-[10px] font-medium tabular-nums"
            >
              {count}
            </Badge>
          )}
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {label}
          </span>
          {isActive && (
            <button
              onClick={onClear}
              className="text-xs text-[hsl(var(--brand))] hover:text-[hsl(var(--brand-hover))] transition-colors"
            >
              Clear
            </button>
          )}
        </div>
        <ScrollArea className="max-h-72">
          <div className="p-1">
            {options.length === 0 ? (
              <div className="px-3 py-6 text-center text-meta">
                No options available
              </div>
            ) : (
              options.map((opt) => {
                const isSelected = selected.has(opt.value)
                return (
                  <label
                    key={opt.value}
                    className={cn(
                      'flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-sm transition-colors',
                      'hover:bg-accent/50',
                      isSelected && 'bg-accent/30'
                    )}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => onToggle(opt.value)}
                      aria-label={opt.label}
                    />
                    {opt.leading}
                    <span className="flex-1 truncate">{opt.label}</span>
                    {isSelected && (
                      <Check className="h-3.5 w-3.5 text-[hsl(var(--brand))]" />
                    )}
                  </label>
                )
              })
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  )
}
